import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"
import { AlertCircle, Printer, Receipt, Utensils, Plus, X, Phone } from "lucide-react"
import { API_URL } from "../../api/config"

// Custom Modal Component
const Modal = ({ isOpen, onClose, title, message, actions = [] }) => {
  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="py-2">
          <p className="text-gray-600">{message}</p>
        </div>

        <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-100">
          {actions.length === 0 ? (
            <button onClick={onClose} className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded transition">
              OK
            </button>
          ) : (
            actions.map((action, index) => (
              <button
                key={index}
                onClick={() => {
                  if (action.onPress) action.onPress()
                  if (!action.keepOpen) onClose()
                }}
                className={
                  action.isDestructive
                    ? "bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded transition"
                    : "bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded transition"
                }
              >
                {action.text}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

const NewBill = () => {
  const [tables, setTables] = useState([])
  const [holdBills, setHoldBills] = useState({})
  const [loading, setLoading] = useState(true)
  const [processingBill, setProcessingBill] = useState(false)
  const { userToken, restaurantId } = useAuth()
  const navigate = useNavigate()

  // Modal state
  const [modal, setModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    actions: [],
  })

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      await Promise.all([fetchTables(), fetchHoldBills()])
      setLoading(false)
    }

    loadData()
  }, [])

  const fetchTables = async () => {
    try {
      const response = await fetch(`${API_URL}/tables`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      })
      if (!response.ok) throw new Error("Failed to fetch tables")
      const data = await response.json()
      setTables(data)
    } catch (err) {
      showModal("Error", "Failed to load tables. Please try again.")
    }
  }

  const fetchHoldBills = async () => {
    try {
      const response = await fetch(`${API_URL}/hold-bills?restaurantId=${restaurantId}`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      })
      if (!response.ok) throw new Error("Failed to fetch hold bills")
      const data = await response.json()
      const holdBillsByTable = data.reduce((acc, bill) => {
        if (bill.status === "HOLD") {
          if (!acc[bill.tableId]) {
            acc[bill.tableId] = []
          }
          acc[bill.tableId].push(bill)
        }
        return acc
      }, {})
      setHoldBills(holdBillsByTable)
    } catch (err) {
      showModal("Error", "Failed to load hold bills. Please try again.")
    }
  }

  const showModal = (title, message, actions = []) => {
    setModal({
      isOpen: true,
      title,
      message,
      actions,
    })
  }

  const closeModal = () => {
    setModal({
      ...modal,
      isOpen: false,
    })
  }

  const handleTableSelect = async (tableId) => {
    try {
      const existingHoldBills = holdBills[tableId] || []
      if (existingHoldBills.length > 0) {
        showModal(
          "Table Has Hold Bills",
          "This table has hold bills. Would you like to add more items or create a new bill?",
          [
            {
              text: "Add Items",
              onPress: () => {
                // User wants to add more items
                const latestHoldBill = existingHoldBills[0]
                navigate("/menu-selection", {
                  state: {
                    tableId,
                    existingHoldBillId: latestHoldBill._id,
                    existingItems: latestHoldBill.items,
                    isAddingItems: true,
                  },
                })
              },
            },
            {
              text: "Create New Bill",
              onPress: () => {
                // Show delete confirmation dialog first before creating new bill
                showModal(
                  "Confirm Delete",
                  `This will delete ${existingHoldBills.length > 1 ? "all existing" : "the existing"} hold bill${existingHoldBills.length > 1 ? "s" : ""} for this table. Continue?`,
                  [
                    {
                      text: "Cancel",
                      isDestructive: false,
                    },
                    {
                      text: "Delete & Continue",
                      isDestructive: true,
                      onPress: async () => {
                        try {
                          setProcessingBill(true)

                          // Delete all existing hold bills for this table
                          for (const bill of existingHoldBills) {
                            const deleteResponse = await fetch(`${API_URL}/hold-bills/${bill._id}`, {
                              method: "DELETE",
                              headers: {
                                Authorization: `Bearer ${userToken}`,
                              },
                            })
                            if (!deleteResponse.ok) {
                              throw new Error(`Failed to delete hold bill: ${bill._id}`)
                            }
                          }

                          await updateTableStatus(tableId)
                          await fetchHoldBills()
                          setProcessingBill(false)
                          navigate("/new-bill", { state: { tableId } })
                        } catch (error) {
                          setProcessingBill(false)
                          showModal("Error", "Failed to delete existing bills. Please try again.")
                        }
                      },
                    },
                  ],
                )
              },
              keepOpen: true,
            },
          ],
        )
      } else {
        await updateTableStatus(tableId)
        navigate("/menu-selection", { state: { tableId } })
      }
    } catch (err) {
      showModal("Error", err.message || "Failed to select table. Please try again.")
    }
  }

  const updateTableStatus = async (tableId) => {
    const tableResponse = await fetch(`${API_URL}/tables/${tableId}/status`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({ status: "Occupied" }),
    })
    if (!tableResponse.ok) throw new Error("Failed to update table status")
  }

  const handlePrintKOT = (tableId) => {
    if (!holdBills[tableId] || holdBills[tableId].length === 0) {
      showModal("Error", "No items to print")
      return
    }
    const tableObject = tables.find((table) => table._id === tableId)
    if (!tableObject) {
      showModal("Error", "Table information not found")
      return
    }
    const bill = holdBills[tableId][0]
    // Pass tableNumber along
    const billWithTableNumber = { ...bill, tableNumber: tableObject.tableNumber }

    navigate(`/print-kot/${tableId}`, {
      state: {
        tableId,
        tableNumber: tableObject.tableNumber,
        currentBill: billWithTableNumber,
      },
    })
  }

  const handleSaveBill = async (tableId) => {
    try {
      const currentHoldBills = holdBills[tableId] || []
      if (currentHoldBills.length === 0) {
        throw new Error("No active bill found for this table")
      }
      const currentBill = currentHoldBills[0]
      const discountPercentage = currentBill.discountPercentage || 0

      showModal("Save Bill", "Do you want to save and print the bill?", [
        {
          text: "Cancel",
          isDestructive: false,
        },
        {
          text: "Save & Print",
          onPress: async () => {
            setProcessingBill(true)
            try {
              const saveResponse = await fetch(`${API_URL}/bills/${tableId}/save`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${userToken}`,
                },
                body: JSON.stringify({
                  discountPercentage,
                  items: currentBill.items,
                  names: currentBill.names,
                }),
              })
              const saveData = await saveResponse.json()
              if (!saveResponse.ok) {
                throw new Error(saveData.message || "Failed to save bill")
              }

              const deleteResponse = await fetch(`${API_URL}/hold-bills/${currentBill._id}`, {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${userToken}`,
                },
              })
              const deleteData = await deleteResponse.json()
              if (!deleteResponse.ok) {
                throw new Error(deleteData.message || "Failed to delete hold bill")
              }

              // Remove that bill from state
              setHoldBills((prev) => {
                const updated = { ...prev }
                updated[tableId] = updated[tableId].filter((b) => b._id !== currentBill._id)
                return updated
              })

              setProcessingBill(false)

              navigate(`/print-bill/${saveData._id}`, {
                state: {
                  billId: saveData._id,
                  billData: {
                    ...currentBill,
                    savedBillId: saveData._id,
                    billNumber: saveData.billNumber,
                  },
                },
              })
            } catch (error) {
              setProcessingBill(false)
              showModal("Error", `Failed to process bill: ${error.message}. Please try again or contact support.`)
            }
          },
        },
      ])
    } catch (error) {
      showModal("Error", error.message || "Failed to process bill. Please try again.")
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header Section */}
      <div className="py-6">
        <div className="flex flex-col items-center justify-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4 text-center">Select Table for New Bill</h1>
          <button
            className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-6 rounded-full flex items-center transition duration-300 ease-in-out"
            onClick={() => navigate("/table-manager")}
          >
            <Utensils className="mr-2 h-5 w-5" />
            Manage Tables
          </button>
        </div>
      </div>

      {/* Loading Spinner */}
      {loading ? (
        <div className="flex justify-center items-center flex-grow">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
        </div>
      ) : (
        <div className="flex-grow px-4 pb-6">
          {/* Grid of Tables */}
          {tables.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {tables.map((table) => {
                const hasHoldBills = holdBills[table._id]?.length > 0
                const tableBills = holdBills[table._id] || []
                const totalAmount = tableBills.reduce((sum, bill) => sum + bill.totalAmount, 0)

                return (
                  <div
                    key={table._id}
                    className={`bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1
                      ${table.status === "Occupied" ? "bg-gray-50" : "bg-white"}
                      ${hasHoldBills ? "border-2 border-blue-500 bg-blue-50" : ""}
                    `}
                  >
                    <div className="p-5">
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold">Table {table.tableNumber}</h2>
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${
                            table.status === "Occupied" ? "bg-red-100 text-red-600" : "bg-green-100 text-green-600"
                          }`}
                        >
                          {table.status === "Occupied" ? "In Use" : "Available"}
                        </span>
                      </div>

                      <button
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md mb-3 transition duration-300 ease-in-out flex justify-center items-center"
                        onClick={() => handleTableSelect(table._id)}
                      >
                        <Plus className="mr-2 h-5 w-5" />
                        {hasHoldBills ? "Manage Bill" : "New Bill"}
                      </button>

                      {hasHoldBills && (
                        <div className="flex justify-between gap-2 mb-4">
                          <button
                            className="w-1/2 bg-green-600 hover:bg-green-700 text-white py-2 rounded-md flex justify-center items-center text-sm"
                            onClick={() => handlePrintKOT(table._id)}
                          >
                            <Printer className="mr-1 h-4 w-4" />
                            Print KOT
                          </button>

                          <button
                            className="w-1/2 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md flex justify-center items-center text-sm"
                            onClick={() => handleSaveBill(table._id)}
                          >
                            <Receipt className="mr-1 h-4 w-4" />
                            Save Bill
                          </button>
                        </div>
                      )}
                    </div>

                    {hasHoldBills && (
                      <div className="bg-gray-50 p-4 border-t border-gray-200">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="font-semibold text-gray-700">Hold Bills ({tableBills.length})</h3>
                          <span className="text-lg font-bold text-blue-600">₹{totalAmount.toFixed(2)}</span>
                        </div>

                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {tableBills.map((bill) => (
                            <div key={bill._id} className="bg-white p-3 rounded-md shadow-sm">
                              <div className="flex justify-between items-center mb-2">
                                <span className="text-xs font-semibold text-gray-500">
                                  {new Date(bill.createdAt).toLocaleTimeString()}
                                </span>
                                <div className="flex items-center gap-2">
                                  {bill.mobileNumber && (
                                    <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                      <Phone size={12} />
                                      <span>{bill.mobileNumber}</span>
                                    </div>
                                  )}
                                  {bill.discountPercentage > 0 && (
                                    <span className="bg-orange-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                                      {bill.discountPercentage}% OFF
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="space-y-2 mb-3">
                                {bill.items.map((item, index) => (
                                  <div key={index} className="flex justify-between text-sm">
                                    <div className="flex-1 pr-2">
                                      <p className="text-gray-800">
                                        {item.itemName} <span className="font-medium">×{item.quantity}</span>
                                        {item.variant && (
                                          <span className="text-gray-500 text-xs ml-1">({item.variant.name})</span>
                                        )}
                                      </p>
                                      {item.names && (
                                        <p className="text-gray-500 text-xs italic">names: {item.names}</p>
                                      )}
                                    </div>
                                    <span className="font-medium">₹{(item.price * item.quantity).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>

                              <div className="border-t border-gray-100 pt-2 text-sm">
                                {bill.discountPercentage > 0 && (
                                  <div className="flex justify-between text-orange-600 mb-1">
                                    <span>Discount Applied:</span>
                                    <span className="font-bold">-₹{bill.discountAmount?.toFixed(2) || "0.00"}</span>
                                  </div>
                                )}
                                {bill.names && (
                                  <p className="text-gray-500 text-xs italic mt-2 pt-2 border-t border-gray-100">
                                    Bill name: {bill.names}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-4" />
              <h2 className="text-xl font-bold text-gray-800 mb-2">No Tables Found</h2>
              <p className="text-gray-600 mb-4">There are no tables set up yet. Create some tables to get started.</p>
              <button
                className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-6 rounded-full transition duration-300 ease-in-out"
                onClick={() => navigate("/table-manager")}
              >
                Manage Tables
              </button>
            </div>
          )}
        </div>
      )}

      {/* Processing Bill Modal */}
      {processingBill && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4" />
            <p className="text-lg font-medium">Processing bill...</p>
          </div>
        </div>
      )}

      {/* Custom Modal */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        actions={modal.actions}
      />
    </div>
  )
}

export default NewBill
